import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";

import { invoiceQueryKeys } from "../api/queryKeys";

import { instance } from "@/lib/axios/instance";
import { ChannelMonthType } from "@/types/response/ChannelMonthType";
import { PagedResponse } from "@/types/response/PagedResponse";

type UseChannelMonthParams = {
  channelId: string;
  year: string;
  month: string;
  tenantId?: string | null;
  descending?: boolean;
  orderBy?: string;
  page?: number;
  pageSize?: number;
  search?: string;
  isInvoiced?: boolean;
};

/**
 * Fetches channel month data for a specific channel, year, and month.
 * Used for displaying channel spending and invoice data by month.
 */
export function useChannelMonth(params: UseChannelMonthParams) {
  const queryKey = [
    ...invoiceQueryKeys.cache.channelMonths.byChannelYearMonth(
      params.channelId,
      params.year,
      params.month
    ),
    params.descending,
    params.orderBy,
    params.page,
    params.search,
    params.pageSize,
    params.tenantId,
    params.isInvoiced,
  ];

  const queryResult = useQuery<PagedResponse<ChannelMonthType>, AxiosError>({
    queryKey,
    queryFn: () => {
      return instance
        .get<PagedResponse<ChannelMonthType>>(
          `/api/v2/Channels/${params.channelId}/${params.year}/${params.month}`,
          {
            params: {
              descending: params.descending,
              isInvoiced: params.isInvoiced,
              orderBy: params.orderBy,
              page: params.page,
              search: params.search,
              pageSize: params.pageSize,
              tenantId: params.tenantId || undefined,
            },
          }
        )
        .then((res) => res.data);
    },
    enabled: !!params.channelId && !!params.year && !!params.month,
    placeholderData: keepPreviousData,
    refetchOnReconnect: false,
    refetchOnMount: true, // Always refetch on mount to ensure fresh data after invalidation
    staleTime: 20 * 60 * 1000,
  });

  return queryResult;
}
